/**
 * BookingRescheduleDialog
 * ------------------------
 * Dialog léger pour sélectionner un nouveau créneau à partir des disponibilités
 * d'un coach (endpoint /api/marketplace/coaches/:id/availability).
 *
 * Props:
 *   - open: bool
 *   - onOpenChange: (bool) => void
 *   - booking: { booking_id, coach_user_id, date, start_time }
 *   - token: string (JWT)
 *   - onConfirm: async ({ new_date, new_start_time }) => void (throws on error)
 *   - title?: string
 *   - confirmLabel?: string
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { formatDateLongFR } from '../utils/formatFR';
import { toast } from 'sonner';

const API = getApiUrl();

export default function BookingRescheduleDialog({
  open,
  onOpenChange,
  booking,
  token,
  onConfirm,
  title = 'Choisir un nouveau créneau',
  confirmLabel = 'Confirmer le report',
}) {
  const [loading, setLoading] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [selected, setSelected] = useState(null); // { date, start_time }
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !booking?.coach_user_id) return;
    const fetchSlots = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/marketplace/coaches/${booking.coach_user_id}/availability?days=30`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Impossible de charger les créneaux');
        const data = await res.json();
        setSlotsByDate(data.availability || {});
        setSelected(null);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
  }, [open, booking?.coach_user_id, token]);

  const dates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  const confirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await onConfirm({ new_date: selected.date, new_start_time: selected.start_time });
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1C1147]">
            <CalendarIcon className="w-5 h-5 text-[#6D28D9]" /> {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#4B3869]">
            Créneau actuel : <strong>{formatDateLongFR(booking?.date)}</strong> à{' '}
            <strong>{booking?.start_time}</strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#6D28D9]" />
          </div>
        ) : dates.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#4B3869]">
            Aucun créneau disponible dans les 30 prochains jours.
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto pr-2 space-y-4">
            {dates.map((date) => (
              <div key={date}>
                <div className="flex items-center gap-2 text-sm font-bold text-[#1C1147] mb-2">
                  <CalendarIcon className="w-4 h-4 text-[#6D28D9]" />
                  {formatDateLongFR(date)}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slotsByDate[date].map((slot) => {
                    const active =
                      selected?.date === date && selected?.start_time === slot.start_time;
                    const isCurrent =
                      booking?.date === date && booking?.start_time === slot.start_time;
                    return (
                      <button
                        key={`${date}-${slot.start_time}`}
                        disabled={isCurrent}
                        data-testid={`slot-${date}-${slot.start_time}`}
                        onClick={() => setSelected({ date, start_time: slot.start_time })}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-all flex items-center justify-center gap-1
                          ${active
                            ? 'bg-[#6D28D9] text-white border-[#6D28D9] shadow-md'
                            : isCurrent
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                            : 'bg-white text-[#1C1147] border-gray-200 hover:border-[#6D28D9] hover:bg-violet-50'}`}
                      >
                        <Clock className="w-3 h-3" />
                        {slot.start_time}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}
            data-testid="reschedule-cancel-btn">
            Fermer
          </Button>
          <Button
            disabled={!selected || submitting}
            onClick={confirm}
            data-testid="reschedule-confirm-btn"
            className="bg-[#6D28D9] hover:bg-[#5B21B6] text-white"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
